import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useRecordPayrollAdvance } from '../../lib/payroll/use-payroll.js';
import { formatCurrency } from '../../lib/format.js';
import { safeRupees } from './safe-rupees.js';

/**
 * Cash handed to one person part-way through the cycle.
 *
 * The staff member is fixed to the row this was opened from and is shown, not chosen: an
 * advance recorded against the wrong person is money that comes off somebody else's wages.
 * On success the payroll queries are invalidated and the table's figures come back from the
 * server — nothing on screen is edited in place.
 */
export interface PayrollAdvanceDialogProps {
  staffUserId: string;
  staffName: string;
  onClose: () => void;
}

export function PayrollAdvanceDialog({
  staffUserId,
  staffName,
  onClose,
}: PayrollAdvanceDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordPayrollAdvance(staffUserId);

  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const amountPaise = safeRupees(amount);
    if (amountPaise <= 0) {
      setError(t('payroll.amountRequired'));
      return;
    }
    setError(null);
    try {
      await record.mutateAsync({
        amountPaise,
        ...(paidAt ? { paidAt } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast({
        title: t('payroll.advanceRecorded', {
          amount: formatCurrency(amountPaise),
          name: staffName,
        }),
        variant: 'success',
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payroll.recordAdvance')}</DialogTitle>
          <DialogDescription>{t('payroll.advanceDialogHint')}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-muted-foreground">{t('payroll.staffMember')}</span>
            <span className="font-medium">{staffName}</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-advance-amount">{t('payroll.amount')}</Label>
            <Input
              id="payroll-advance-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-advance-date">{t('payroll.advanceDate')}</Label>
            <Input
              id="payroll-advance-date"
              type="date"
              value={paidAt}
              onChange={(event) => setPaidAt(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payroll-advance-note">{t('payroll.note')}</Label>
            <Input
              id="payroll-advance-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={record.isPending} onClick={() => void submit()}>
            {t('payroll.advanceSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
